import { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useTenantFormat } from '../../hooks/useTenantFormat';
import { useToast } from '../../hooks/useToast';
import { api } from '../../lib/api';
import Drawer from '../../components/Drawer';
import { ArrowLeft, Edit3, Play, UserPlus, X, Check, AlertCircle, Calendar, Users, Zap } from 'lucide-react';

function StatusPill({ status }) {
  const cls = status === 'active' ? 'bg-emerald-50 text-emerald-700 border-emerald-200' : 'bg-gray-100 text-gray-500 border-gray-200';
  return <span className={`px-2.5 py-1 rounded-full text-[11px] font-bold uppercase border ${cls}`}>{status || 'active'}</span>;
}

/* ── Assign Students Modal ── */
function AssignModal({ open, onClose, token, planId, onSuccess }) {
  const toast = useToast();
  const [students, setStudents] = useState([]);
  const [selected, setSelected] = useState(new Set());
  const [startDate, setStartDate] = useState(new Date().toISOString().slice(0, 10));
  const [endDate, setEndDate] = useState('');
  const [q, setQ] = useState('');
  const [saving, setSaving] = useState(false);
  const [loadingStudents, setLoadingStudents] = useState(false);

  useEffect(() => {
    if (open && token) {
      setSelected(new Set()); setQ('');
      setStartDate(new Date().toISOString().slice(0, 10)); setEndDate('');
      setLoadingStudents(true);
      api.customersList(token, { customer_type: 'student', active_status: 'active', limit: 200 })
        .then(r => setStudents(r.items || []))
        .catch(() => setStudents([]))
        .finally(() => setLoadingStudents(false));
    }
  }, [open, token]);

  const toggle = id => setSelected(prev => { const n = new Set(prev); n.has(id) ? n.delete(id) : n.add(id); return n; });
  const filtered = students.filter(s => !q || s.name?.toLowerCase().includes(q.toLowerCase()) || (s.admission_no || '').toLowerCase().includes(q.toLowerCase()));

  const submit = async () => {
    if (selected.size === 0) { toast.show('Select at least one student', 'error'); return; }
    setSaving(true);
    try {
      await api.feePlanAssign(token, planId, {
        student_customer_ids: [...selected],
        effective_start_date: startDate,
        effective_end_date: endDate || undefined,
      });
      toast.show(`${selected.size} student(s) assigned`, 'success');
      onSuccess();
    } catch (e) { toast.show(e?.message || 'Assign failed', 'error'); } finally { setSaving(false); }
  };

  if (!open) return null;
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/30 backdrop-blur-sm" data-testid="assign-students-modal">
      <div className="bg-white rounded-2xl shadow-2xl border border-gray-200 w-full max-w-lg p-6 max-h-[80vh] flex flex-col" onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-bold text-gray-900">Assign Students</h3>
          <button onClick={onClose} className="p-2 rounded-xl hover:bg-gray-100 text-gray-400"><X size={20} /></button>
        </div>
        <div className="grid grid-cols-2 gap-3 mb-4">
          <label className="block"><span className="text-xs font-bold text-gray-500 uppercase">Start Date *</span>
            <input data-testid="assign-start" type="date" className="mt-1 w-full px-3 py-2 rounded-xl border border-gray-200 text-sm" value={startDate} onChange={e => setStartDate(e.target.value)} /></label>
          <label className="block"><span className="text-xs font-bold text-gray-500 uppercase">End Date (optional)</span>
            <input data-testid="assign-end" type="date" className="mt-1 w-full px-3 py-2 rounded-xl border border-gray-200 text-sm" value={endDate} onChange={e => setEndDate(e.target.value)} /></label>
        </div>
        <input data-testid="assign-search" placeholder="Search students..." value={q} onChange={e => setQ(e.target.value)}
          className="w-full px-3 py-2 rounded-xl border border-gray-200 text-sm mb-3 outline-none focus:ring-2 focus:ring-indigo-200" />
        <div className="flex-1 overflow-y-auto border border-gray-200 rounded-xl divide-y divide-gray-100 min-h-[120px] max-h-[300px]">
          {loadingStudents ? <div className="flex justify-center py-8"><div className="w-6 h-6 border-2 border-indigo-400 border-t-transparent rounded-full animate-spin" /></div> :
            filtered.length === 0 ? <p className="text-gray-400 text-sm text-center py-6">No students found</p> :
            filtered.map(s => (
              <label key={s.id} className="flex items-center gap-3 px-4 py-2.5 hover:bg-indigo-50/50 cursor-pointer transition-colors" data-testid={`assign-student-${s.id}`}>
                <input type="checkbox" checked={selected.has(s.id)} onChange={() => toggle(s.id)}
                  className="w-4 h-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-200" />
                <div className="min-w-0 flex-1">
                  <p className="text-gray-900 text-sm font-semibold truncate">{s.name}</p>
                  <p className="text-gray-400 text-xs">{[s.grade && `Grade ${s.grade}`, s.batch && `Batch ${s.batch}`, s.admission_no].filter(Boolean).join(' · ')}</p>
                </div>
              </label>
            ))}
        </div>
        <div className="flex items-center justify-between mt-4">
          <span className="text-gray-500 text-xs font-semibold">{selected.size} selected</span>
          <button onClick={submit} disabled={saving || selected.size === 0} data-testid="assign-submit"
            className="px-6 py-2.5 rounded-xl bg-indigo-600 text-white text-sm font-bold disabled:opacity-50 hover:bg-indigo-700 transition-colors">
            {saving ? 'Assigning...' : 'Assign'}
          </button>
        </div>
      </div>
    </div>
  );
}

/* ── Run Billing Modal ── */
function RunBillingModal({ open, onClose, token, planId, planName, onSuccess }) {
  const toast = useToast();
  const now = new Date();
  const defaultPeriod = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
  const [period, setPeriod] = useState(defaultPeriod);
  const [dryRun, setDryRun] = useState(true);
  const [running, setRunning] = useState(false);
  const [result, setResult] = useState(null);

  useEffect(() => { if (open) { setPeriod(defaultPeriod); setDryRun(true); setResult(null); } }, [open, defaultPeriod]);

  const run = async (forceDryRun) => {
    const dr = forceDryRun !== undefined ? forceDryRun : dryRun;
    setRunning(true);
    try {
      const r = await api.feePlanRunBilling(token, planId, { period, dry_run: dr });
      setResult({ ...r, was_dry: dr });
      if (!dr && (r.invoices_created || r.students_billed)) onSuccess();
      toast.show(dr ? 'Dry run complete' : `Billing complete — ${r.invoices_created || 0} invoices created`, 'success');
    } catch (e) { toast.show(e?.message || 'Billing failed', 'error'); } finally { setRunning(false); }
  };

  if (!open) return null;
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/30 backdrop-blur-sm" data-testid="run-billing-modal">
      <div className="bg-white rounded-2xl shadow-2xl border border-gray-200 w-full max-w-md p-6" onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-5">
          <h3 className="text-lg font-bold text-gray-900 flex items-center gap-2"><Zap size={20} className="text-amber-500" /> Run Billing</h3>
          <button onClick={onClose} className="p-2 rounded-xl hover:bg-gray-100 text-gray-400"><X size={20} /></button>
        </div>

        {!result ? (
          <div className="space-y-4">
            <p className="text-gray-500 text-sm">Generate fee invoices for <span className="font-bold text-gray-900">{planName}</span>.</p>
            <label className="block"><span className="text-xs font-bold text-gray-500 uppercase">Billing Period</span>
              <input data-testid="billing-period" type="month" className="mt-1 w-full px-4 py-2.5 rounded-xl border border-gray-200 text-sm outline-none focus:ring-2 focus:ring-indigo-200" value={period} onChange={e => setPeriod(e.target.value)} /></label>
            <label className="flex items-center gap-3 cursor-pointer" data-testid="billing-dry-run-toggle">
              <div className={`relative w-11 h-6 rounded-full transition-colors ${dryRun ? 'bg-amber-400' : 'bg-gray-300'}`} onClick={() => setDryRun(!dryRun)}>
                <div className={`absolute top-0.5 w-5 h-5 bg-white rounded-full shadow transition-transform ${dryRun ? 'left-[22px]' : 'left-0.5'}`} />
              </div>
              <div>
                <span className="text-sm font-semibold text-gray-700">{dryRun ? 'Dry Run (Preview)' : 'Real Run (Create Invoices)'}</span>
                <p className="text-xs text-gray-400">{dryRun ? 'No invoices will be created — preview only' : 'Invoices will be created and sent to students'}</p>
              </div>
            </label>
            <button onClick={() => run()} disabled={running} data-testid="billing-run-btn"
              className={`w-full h-11 rounded-xl font-bold text-sm transition-all disabled:opacity-50 ${dryRun ? 'bg-amber-500 hover:bg-amber-600 text-white' : 'bg-indigo-600 hover:bg-indigo-700 text-white'}`}>
              {running ? 'Running...' : dryRun ? 'Preview Billing' : 'Run Billing Now'}
            </button>
          </div>
        ) : (
          <div className="space-y-4" data-testid="billing-result">
            <div className={`flex items-center gap-2 px-3 py-2 rounded-xl text-sm font-semibold ${result.was_dry ? 'bg-amber-50 text-amber-700 border border-amber-200' : 'bg-emerald-50 text-emerald-700 border border-emerald-200'}`}>
              {result.was_dry ? <><AlertCircle size={16} /> Dry Run Preview</> : <><Check size={16} /> Billing Complete</>}
            </div>
            <div className="grid grid-cols-2 gap-3" data-testid="billing-result-cards">
              {[
                ['Invoices Created', result.invoices_created, 'text-indigo-600 bg-indigo-50'],
                ['Students Billed', result.students_billed, 'text-emerald-600 bg-emerald-50'],
                ['Skipped', result.skipped_already_billed ?? result.skipped, 'text-amber-600 bg-amber-50'],
                ['Errors', (result.errors?.length || 0), 'text-red-600 bg-red-50'],
              ].map(([label, count, cls]) => (
                <div key={label} className={`rounded-xl p-3 text-center ${cls.split(' ').slice(1).join(' ')}`}>
                  <p className={`text-2xl font-extrabold tabular-nums ${cls.split(' ')[0]}`}>{count ?? 0}</p>
                  <p className="text-xs font-semibold text-gray-500 mt-1">{label}</p>
                </div>
              ))}
            </div>
            {result.errors?.length > 0 && (
              <div className="max-h-32 overflow-y-auto border border-red-200 rounded-xl p-3 space-y-1">
                {result.errors.map((err, i) => (
                  <div key={i} className="flex items-start gap-2 text-xs text-red-600">
                    <AlertCircle size={12} className="flex-shrink-0 mt-0.5" />
                    <span>{err.message || err.error || JSON.stringify(err)}</span>
                  </div>
                ))}
              </div>
            )}
            <div className="flex gap-3">
              {result.was_dry && (
                <button onClick={() => run(false)} disabled={running} data-testid="billing-run-real"
                  className="flex-1 h-10 rounded-xl bg-indigo-600 text-white font-bold text-sm hover:bg-indigo-700 disabled:opacity-50 transition-colors">
                  {running ? 'Running...' : 'Run for Real'}
                </button>
              )}
              <button onClick={onClose} className="flex-1 h-10 rounded-xl border border-gray-200 text-gray-700 font-semibold text-sm hover:bg-gray-50 transition-colors">
                {result.was_dry ? 'Cancel' : 'Close'}
              </button>
            </div>
            {!result.was_dry && result.log_id && (
              <a href={`/school/billing-logs/${result.log_id}`} className="block text-center text-indigo-600 text-xs font-semibold hover:underline" data-testid="billing-view-log">View Billing Log</a>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

/* ── Edit Plan Drawer ── */
function EditPlanDrawer({ open, onClose, plan, token, onSuccess, currencySymbol }) {
  const toast = useToast();
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));
  useEffect(() => { if (open && plan) setForm({ name: plan.name || '', description: plan.description || '', billing_day: String(plan.billing_day || 1) }); }, [open, plan]);
  const submit = async () => {
    setSaving(true);
    try {
      await api.feePlanPatch(token, plan.id, { name: form.name.trim(), description: form.description.trim() || undefined, billing_day: parseInt(form.billing_day) || 1 });
      toast.show('Plan updated', 'success'); onSuccess();
    } catch (e) { toast.show(e?.message || 'Update failed', 'error'); } finally { setSaving(false); }
  };
  return (
    <Drawer open={open} onClose={onClose} title="Edit Fee Plan">
      <div className="space-y-4">
        <label className="block"><span className="text-xs font-bold text-gray-500 uppercase">Name</span>
          <input data-testid="edit-fp-name" className="mt-1 w-full px-4 py-2.5 rounded-xl border border-gray-200 text-sm outline-none focus:ring-2 focus:ring-indigo-200" value={form.name || ''} onChange={e => set('name', e.target.value)} /></label>
        <label className="block"><span className="text-xs font-bold text-gray-500 uppercase">Description</span>
          <input data-testid="edit-fp-desc" className="mt-1 w-full px-4 py-2.5 rounded-xl border border-gray-200 text-sm outline-none focus:ring-2 focus:ring-indigo-200" value={form.description || ''} onChange={e => set('description', e.target.value)} /></label>
        <label className="block"><span className="text-xs font-bold text-gray-500 uppercase">Billing Day</span>
          <input data-testid="edit-fp-day" type="number" min="1" max="28" className="mt-1 w-full px-4 py-2.5 rounded-xl border border-gray-200 text-sm outline-none focus:ring-2 focus:ring-indigo-200" value={form.billing_day || ''} onChange={e => set('billing_day', e.target.value)} /></label>
        <button onClick={submit} disabled={saving} data-testid="edit-fp-submit" className="w-full h-12 rounded-xl bg-gradient-to-r from-indigo-600 to-purple-600 text-white font-bold text-sm disabled:opacity-50 hover:shadow-lg transition-all">{saving ? 'Saving...' : 'Save'}</button>
      </div>
    </Drawer>
  );
}

export default function FeePlanDetail() {
  const { id } = useParams();
  const { token } = useAuth();
  const navigate = useNavigate();
  const toast = useToast();
  const { formatMoney, currencySymbol } = useTenantFormat();

  const [plan, setPlan] = useState(null);
  const [assignments, setAssignments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [assignOpen, setAssignOpen] = useState(false);
  const [billingOpen, setBillingOpen] = useState(false);
  const [editOpen, setEditOpen] = useState(false);

  const load = useCallback(async () => {
    if (!token || !id) return;
    setLoading(true);
    try {
      const [p, a] = await Promise.all([
        api.feePlanGet(token, id),
        api.feePlanAssignments(token, id, { limit: 100 }).catch(() => ({ items: [] })),
      ]);
      setPlan(p);
      setAssignments(a.items || a || []);
    } catch (e) { toast.show(e?.message || 'Load failed', 'error'); }
    finally { setLoading(false); }
  }, [token, id, toast]);

  useEffect(() => { load(); }, [load]);

  const toggleStatus = async () => {
    if (!plan) return;
    const ns = plan.active_status === 'active' ? 'inactive' : 'active';
    try {
      await api.feePlanPatch(token, id, { active_status: ns });
      toast.show(`Plan ${ns === 'active' ? 'activated' : 'deactivated'}`, 'success'); load();
    } catch (e) { toast.show(e?.message || 'Update failed', 'error'); }
  };

  const deactivateAssignment = async (aId) => {
    try {
      await api.feePlanAssignmentDeactivate(token, aId);
      toast.show('Assignment deactivated', 'success'); load();
    } catch (e) { toast.show(e?.message || 'Failed', 'error'); }
  };

  if (loading) return <div className="flex justify-center py-20"><div className="w-8 h-8 border-2 border-indigo-400 border-t-transparent rounded-full animate-spin" /></div>;
  if (!plan) return <div className="p-8 text-center text-gray-500">Fee plan not found</div>;

  const items = plan.items || [];

  return (
    <div className="p-6 lg:p-8 max-w-5xl mx-auto" data-testid="fee-plan-detail-page">
      <button onClick={() => navigate('/school/fee-plans')} data-testid="fp-back" className="flex items-center gap-2 text-gray-500 text-sm font-semibold mb-5 hover:text-gray-700"><ArrowLeft size={16} /> Back to Fee Plans</button>

      {/* Header */}
      <div className="bg-white border border-gray-200 rounded-2xl shadow-sm p-6 mb-6" data-testid="fp-header">
        <div className="flex items-start justify-between">
          <div>
            <h1 className="text-xl font-extrabold text-gray-900">{plan.name}</h1>
            {plan.description && <p className="text-gray-500 text-sm mt-1">{plan.description}</p>}
            <div className="flex items-center gap-3 mt-3">
              <StatusPill status={plan.active_status} />
              <span className="px-2.5 py-1 rounded-full bg-indigo-50 text-indigo-700 text-[11px] font-bold border border-indigo-200" data-testid="fp-billing-day-chip">Billing Day {plan.billing_day}</span>
            </div>
          </div>
          <div className="flex gap-2">
            <button onClick={() => setEditOpen(true)} data-testid="fp-edit-btn" className="flex items-center gap-1.5 px-4 py-2 rounded-xl border border-gray-200 text-gray-700 text-sm font-semibold hover:bg-gray-50"><Edit3 size={14} /> Edit</button>
            <button onClick={toggleStatus} data-testid="fp-toggle-status"
              className={`flex items-center gap-1.5 px-4 py-2 rounded-xl border text-sm font-semibold ${plan.active_status === 'active' ? 'border-red-200 text-red-600 hover:bg-red-50' : 'border-emerald-200 text-emerald-600 hover:bg-emerald-50'}`}>
              {plan.active_status === 'active' ? 'Deactivate' : 'Activate'}
            </button>
          </div>
        </div>
      </div>

      {/* Run Billing CTA */}
      <div className="bg-gradient-to-r from-amber-50 to-orange-50 border border-amber-200 rounded-2xl p-5 mb-6 flex items-center justify-between" data-testid="fp-run-billing-section">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-amber-100 flex items-center justify-center"><Zap size={20} className="text-amber-600" /></div>
          <div>
            <h3 className="text-gray-900 font-bold text-sm">Run Billing Now</h3>
            <p className="text-gray-500 text-xs mt-0.5">Generate fee invoices for assigned students. Preview with dry-run first.</p>
          </div>
        </div>
        <button onClick={() => setBillingOpen(true)} data-testid="fp-run-billing-btn"
          className="flex items-center gap-2 px-5 py-2.5 rounded-xl bg-amber-500 text-white text-sm font-bold hover:bg-amber-600 shadow-lg transition-all">
          <Play size={16} /> Run Billing
        </button>
      </div>

      {/* Fee Items */}
      <div className="bg-white border border-gray-200 rounded-2xl shadow-sm p-5 mb-6" data-testid="fp-items">
        <h3 className="text-gray-500 text-xs font-bold uppercase mb-3">Fee Items ({items.length})</h3>
        {items.length === 0 ? <p className="text-gray-400 text-sm">No fee items defined</p> : (
          <table className="w-full text-sm">
            <thead><tr className="border-b border-gray-200">
              <th className="text-left px-4 py-2 text-gray-500 text-xs font-bold uppercase">Type</th>
              <th className="text-left px-4 py-2 text-gray-500 text-xs font-bold uppercase">Frequency</th>
              <th className="text-right px-4 py-2 text-gray-500 text-xs font-bold uppercase">Amount</th>
              <th className="text-center px-4 py-2 text-gray-500 text-xs font-bold uppercase">Due Day</th>
              <th className="text-right px-4 py-2 text-gray-500 text-xs font-bold uppercase">Tax %</th>
            </tr></thead>
            <tbody>
              {items.map((it, i) => (
                <tr key={i} className="border-b border-gray-100 last:border-0">
                  <td className="px-4 py-2.5 text-gray-900 font-semibold capitalize">{it.fee_type}</td>
                  <td className="px-4 py-2.5 text-gray-600 capitalize">{it.frequency}</td>
                  <td className="px-4 py-2.5 text-gray-900 text-right tabular-nums font-semibold">{formatMoney(it.amount)}</td>
                  <td className="px-4 py-2.5 text-gray-600 text-center tabular-nums">{it.frequency === 'monthly' ? (it.due_day || '—') : '—'}</td>
                  <td className="px-4 py-2.5 text-gray-600 text-right tabular-nums">{it.tax_rate ?? 0}%</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Assigned Students */}
      <div className="bg-white border border-gray-200 rounded-2xl shadow-sm p-5" data-testid="fp-assignments">
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-gray-500 text-xs font-bold uppercase flex items-center gap-2"><Users size={14} /> Assigned Students ({assignments.length})</h3>
          <button onClick={() => setAssignOpen(true)} data-testid="fp-assign-btn"
            className="flex items-center gap-1.5 text-xs font-bold text-indigo-600 hover:text-indigo-800"><UserPlus size={14} /> Assign Students</button>
        </div>
        {assignments.length === 0 ? <p className="text-gray-400 text-sm">No students assigned to this plan</p> : (
          <table className="w-full text-sm">
            <thead><tr className="border-b border-gray-200">
              <th className="text-left px-4 py-2 text-gray-500 text-xs font-bold uppercase">Name</th>
              <th className="text-left px-4 py-2 text-gray-500 text-xs font-bold uppercase">Grade</th>
              <th className="text-left px-4 py-2 text-gray-500 text-xs font-bold uppercase">Batch</th>
              <th className="text-left px-4 py-2 text-gray-500 text-xs font-bold uppercase">Start</th>
              <th className="text-left px-4 py-2 text-gray-500 text-xs font-bold uppercase">Status</th>
              <th className="text-right px-4 py-2 text-gray-500 text-xs font-bold uppercase">Action</th>
            </tr></thead>
            <tbody>
              {assignments.map(a => (
                <tr key={a.id} className="border-b border-gray-100 last:border-0">
                  <td className="px-4 py-2.5 text-gray-900 font-semibold">{a.student_name || a.customer_name || '—'}</td>
                  <td className="px-4 py-2.5 text-gray-600">{a.grade || '—'}</td>
                  <td className="px-4 py-2.5 text-gray-600">{a.batch || '—'}</td>
                  <td className="px-4 py-2.5 text-gray-600">{a.effective_start_date || '—'}</td>
                  <td className="px-4 py-2.5"><StatusPill status={a.active_status || a.status} /></td>
                  <td className="px-4 py-2.5 text-right">
                    {(a.active_status || a.status) === 'active' && (
                      <button onClick={() => deactivateAssignment(a.id)} data-testid={`deactivate-assign-${a.id}`}
                        className="text-xs font-semibold text-red-500 hover:text-red-700">Deactivate</button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      <AssignModal open={assignOpen} onClose={() => setAssignOpen(false)} token={token} planId={id} onSuccess={() => { setAssignOpen(false); load(); }} />
      <RunBillingModal open={billingOpen} onClose={() => setBillingOpen(false)} token={token} planId={id} planName={plan.name} onSuccess={load} />
      <EditPlanDrawer open={editOpen} onClose={() => setEditOpen(false)} plan={plan} token={token} onSuccess={() => { setEditOpen(false); load(); }} currencySymbol={currencySymbol} />
    </div>
  );
}
